import React, {useEffect, useState} from "react";
import {
	Box,
	Grid,
	MenuItem,
	Pagination,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {Alert} from "@mui/material";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import {useTheme} from "@mui/material/styles";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
	EmpUploadRestControllerApiFactory,
	PageDtoPersonDocumentList,
	PersonDocument,
	PersonDocumentList,
	PersonDocumentType,
	PersonEstimation,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {useForm, UseFormProps} from "../../../components/form/form_model";
import {FormContent} from "../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../components/form/form_inputs/form_input";
import FormFile, {fileLocalSave} from "../../../components/form/form_inputs/form_file";
import FormSelect from "../../../components/form/form_inputs/form_select";
import {types} from "../../../components/form/types";
import {dateToString} from "../../../components/utils/dates";
import {
	EmptyState,
	ListCard,
	Page,
	PageHeader,
	Panel,
	SearchField,
	SkeletonRows,
	SoftChip,
	tableContainerSx,
	tableSx,
	Toolbar,
} from "../../../components/kit";
import {PersonDocumentTypeHint} from "../../admin/employee/persondoctype/hint";
import {autoExpirationFor, hasYearMonth, isAutoExpiration, monthsLabel, yearMonthLabel} from "../../admin/employee/persondoctype/labels";

const PAGE_SIZE = 20;

/**
 * Documentos que sube el empleado (hojas de actividad, justificantes…): a la izquierda los ya subidos,
 * que se descargan al pulsar; a la derecha, el formulario de subida. Si el tipo pide año y mes y es una
 * hoja de actividad, enseña las horas estimadas de ese mes. Los subidos se buscan por título y se filtran
 * por tipo en el servidor (#14320).
 */
const EmployeeUploadsPage = () => {
	const apiManager = useApiManager();
	const api = apiManager.factory(EmpUploadRestControllerApiFactory);
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const [trigger, setTrigger] = useState<number>(0);
	const [requiredYearMonth, setRequiredYearMonth] = useState<boolean>(false);
	const [requiredExpiration, setRequiredExpiration] = useState<boolean>(false);
	const [docType, setDocType] = useState<PersonDocumentType | undefined>(undefined);
	const [activityLog, setActivityLog] = useState<boolean>(false);

	const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
	const [year, setYear] = useState<number>(new Date().getFullYear());
	const [estimation, setEstimation] = useState<PersonEstimation | undefined>(undefined);

	const [page, setPage] = useState(1);
	const [result, setResult] = useState<PageDtoPersonDocumentList>();
	const [term, setTerm] = useState("");
	const [query, setQuery] = useState("");
	const [typeFilter, setTypeFilter] = useState<number | "">("");
	const [docTypes, setDocTypes] = useState<PersonDocumentType[]>([]);
	const filtered = query !== "" || typeFilter !== "";

	const form = useForm({
		updater: (bean) => api.saveEmployeeUpload(bean),
		factory: () => {
			return {
				year: new Date().getFullYear(),
				month: new Date().getMonth() + 1,
				person: {id: 0},
			} as PersonDocument;
		},
		postUpdate: (bean) => {
			setTrigger(trigger + 1);
		},
	} as UseFormProps<PersonDocument>);

	useEffect(() => {
		apiManager.execute(api.getEmployeeUploadTypes()).then(setDocTypes);
	}, []);

	// La búsqueda va al servidor cuando se deja de escribir, y vuelve a la primera página
	useEffect(() => {
		const t = setTimeout(() => {
			setQuery(term.trim());
			setPage(1);
		}, 300);
		return () => clearTimeout(t);
	}, [term]);

	useEffect(() => {
		setResult(undefined);
		apiManager.execute(api.pageEmployeeUpload(page - 1, PAGE_SIZE, undefined, query || undefined, typeFilter || undefined))
			.then(setResult)
			.catch(() => setResult({content: [], totalPages: 0, totalElements: 0}));
	}, [page, trigger, query, typeFilter]);

	useEffect(() => {
		if (activityLog) {
			apiManager.execute(api.viewEmployeeEstimationHours(year, month))
				.then(estimation => setEstimation(estimation));
		}
	}, [activityLog, year, month]);

	const download = (bean: PersonDocumentList) => {
		if (bean.id) {
			api.viewEmployeeUpload(bean.id).then((resp) => fileLocalSave(resp));
		}
	};
	const rows = result?.content || [];

	return (
		<Page>
			<PageHeader title="Tus documentos"
						subtitle="Lo que subes a RR. HH.: hojas de actividad, justificantes y otros. Pulsa uno para descargarlo."/>

			<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "minmax(0, 2fr) minmax(0, 1fr)"}, gap: 3, alignItems: "start"}}>
				<Panel title="Subidos" action={result && <Typography variant="body2" color="text.secondary">{result.totalElements}</Typography>}>
					<Toolbar
						left={
							<TextField select size="small" label="Tipo" value={typeFilter}
									   onChange={e => {
										   setTypeFilter(e.target.value === "" ? "" : Number(e.target.value));
										   setPage(1);
									   }}
									   sx={{width: {xs: "100%", md: 240}, "& .MuiOutlinedInput-root": {borderRadius: 2}}}>
								<MenuItem value="">Todos los tipos</MenuItem>
								{docTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
							</TextField>
						}
						right={<SearchField value={term} onChange={setTerm} placeholder="Buscar por título"/>}/>
					{!result ? <SkeletonRows rows={5}/> : rows.length === 0 ? (
						filtered ? (
							<EmptyState icon={<CloudUploadOutlinedIcon/>} title="Ningún documento coincide con la búsqueda"
										description="Prueba con otro título o con otro tipo."/>
						) : (
							<EmptyState icon={<CloudUploadOutlinedIcon/>} title="Aún no has subido ningún documento"
										description="Usa el formulario para subir el primero."/>
						)
					) : compact ? (
						<Stack spacing={1.5}>
							{rows.map(d => (
								<ListCard key={d.id} onClick={() => download(d)}>
									<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
										<Box sx={{minWidth: 0}}>
											<Typography variant="body2" fontWeight={600} sx={{overflowWrap: "anywhere"}}>{d.title}</Typography>
											<Typography variant="caption" color="text.secondary">{dateToString(d.created)}</Typography>
										</Box>
										{d.type?.name && <SoftChip tone="neutral" label={d.type.name}/>}
									</Stack>
								</ListCard>
							))}
						</Stack>
					) : (
						<TableContainer sx={tableContainerSx}>
							<Table size="small" sx={tableSx}>
								<TableHead>
									<TableRow>
										<TableCell sx={{width: 120}}>Fecha</TableCell>
										<TableCell>Título</TableCell>
										<TableCell sx={{width: 190}}>Tipo</TableCell>
										<TableCell sx={{width: 56}}/>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map(d => (
										<TableRow key={d.id} hover onClick={() => download(d)} sx={{cursor: "pointer"}}>
											<TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(d.created)}</TableCell>
											<TableCell sx={{maxWidth: 0}}>
												<Typography variant="body2" fontWeight={600} noWrap title={d.title}>{d.title}</Typography>
											</TableCell>
											<TableCell>{d.type?.name && <SoftChip tone="neutral" label={d.type.name}/>}</TableCell>
											<TableCell align="right" sx={{color: "text.secondary"}}>
												<Tooltip title="Descargar"><FileDownloadOutlinedIcon fontSize="small"/></Tooltip>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
					{(result?.totalPages || 0) > 1 && (
						<Stack alignItems="center" sx={{mt: 3}}>
							<Pagination count={result?.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded"
										size={compact ? "small" : "medium"}/>
						</Stack>
					)}
				</Panel>

				<Panel title="Subir un documento" subtitle="Elige el tipo: según cuál, te pedirá el mes o la caducidad.">
					{/* Qué no se sube (#13891): los datos de salud son categoría especial (RGPD, art. 9) y no
					    los necesitamos; los partes de baja de la Seguridad Social le llegan a la empresa
					    directamente desde el 1-4-2023 (RD 1060/2022). */}
					<Alert severity="warning" icon={<HealthAndSafetyOutlinedIcon fontSize="inherit"/>} sx={{mb: 2.5, borderRadius: 2}}>
						<Typography variant="body2" fontWeight={700} gutterBottom>No subas partes médicos ni informes de salud</Typography>
						<Typography variant="body2" component="div">
							Ni partes de baja o de alta, ni informes, diagnósticos o recetas: son datos de salud y no los
							necesitamos. Los partes de baja de la Seguridad Social ya le llegan a la empresa directamente.
							Para justificar que fuiste al médico, sube solo el <b>justificante de asistencia</b> (el que
							dice que estuviste en consulta y a qué hora, sin diagnóstico) con el tipo «Justificante Ausencia».
						</Typography>
					</Alert>
					<FormContent form={form} withoutPaper hideMetadata={true} hideTopButtons={true}>
						<FormSelect field={"type"} label={"Tipo"} type={types.personDocumentTypesAsEmployee}
									onChange={item => {
										setDocType(item);
										if (item) {
											if (item.requiredYearMonth) setRequiredYearMonth(true);
											else setRequiredYearMonth(false);
											setRequiredExpiration(item.expirationMode === "MANUAL");
											if ((item.name?.toLowerCase().indexOf("actividad") || -1) >= 0) {
												setActivityLog(true);
											} else {
												setActivityLog(false);
											}
										}
									}}/>
						<FormInput field={"title"} label={"Título"} type={"text"}/>
						<PersonDocumentTypeHint type={docType}/>

						{requiredYearMonth && (
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={4}>
									<FormInput field={"year"} label={"Año"} type={"number"} onChange={v => setYear(v)}/>
								</Grid>
								<Grid item xs={4}>
									<FormInput field={"month"} label={"Mes"} type={"number"} onChange={v => setMonth(v)}/>
								</Grid>
								{estimation && (
									<Grid item xs={4}>
										<Typography variant="caption" color="text.secondary" display="block">Estimación</Typography>
										<Typography variant="body2" fontWeight={700}>{estimation.hours} h</Typography>
									</Grid>
								)}
							</Grid>
						)}
						{requiredExpiration && (
							<FormInput field={"expiration"} label={"Caducidad"} type={"date"}/>
						)}
						{/* Caducidad automática (#14381): la pone el servidor al guardar, aquí solo se enseña. Se cuenta
						    desde el día 1 del año y mes del documento (#14387), que el tipo pide. */}
						{isAutoExpiration(docType) && (
							<TextField label="Caducidad" value={hasYearMonth(year, month) ? autoExpirationFor(docType, year, month) : ""}
									   disabled fullWidth variant="filled" sx={{mb: 2}}
									   helperText={hasYearMonth(year, month)
										   ? "Se calcula sola: " + monthsLabel(docType?.expirationMonths) + " desde " + yearMonthLabel(year, month) + ". No se puede cambiar."
										   : "Se calcula sola desde el año y mes del documento: indícalos arriba."}/>
						)}
						<FormFile field={"attachment"} label={"Archivo"}/>
					</FormContent>
				</Panel>
			</Box>
		</Page>
	);
};

export default EmployeeUploadsPage;
